import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card } from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';

type FeedbackItem = {
  id: string;
  timestamp: string;
  username?: string;
  area?: string;
  severity?: string;
  blocks_progress?: boolean;
  route?: string;
  notes?: string;
};

export function AdminFeedback() {
  const { user } = useAuth();
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    start: '', end: '', area: '', username: '', severity: ''
  });
  const [areas, setAreas] = useState<string[]>([]);
  const [users, setUsers] = useState<string[]>([]);
  const [sort, setSort] = useState<'timestamp'|'area'|'username'|'severity'>('timestamp');
  const [order, setOrder] = useState<'asc'|'desc'>('desc');

  const load = async () => {
    try {
      setLoading(true); setError(null);
      const params = new URLSearchParams();
      if (filters.start) params.append('start', filters.start);
      if (filters.end) params.append('end', filters.end);
      if (filters.area) params.append('area', filters.area);
      if (filters.username) params.append('username', filters.username);
      if (filters.severity) params.append('severity', filters.severity);
      params.append('sort', sort);
      params.append('order', order);
      params.append('limit', '1000');
      const res = await fetch(`/api/v1/feedback?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}` }
      });
      if (!res.ok) throw new Error(`Failed to load feedback (${res.status})`);
      const data = await res.json();
      setItems(data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    // Fetch meta for dropdowns
    (async () => {
      try {
        const res = await fetch('/api/v1/feedback/meta', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        setAreas(Array.isArray(data.areas) ? data.areas : []);
        setUsers(Array.isArray(data.users) ? data.users : []);
      } catch {}
    })();
  }, []);

  const onExport = async (format: 'json'|'csv') => {
    try {
      const params = new URLSearchParams();
      params.append('format', format);
      if (filters.start) params.append('start', filters.start);
      if (filters.end) params.append('end', filters.end);
      if (filters.area) params.append('area', filters.area);
      if (filters.username) params.append('username', filters.username);
      if (filters.severity) params.append('severity', filters.severity);
      const res = await fetch(`/api/v1/feedback/export?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}` }
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = format === 'csv' ? 'feedback_export.csv' : 'feedback_export.ndjson';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.message || 'Export failed');
    }
  };

  const onSort = (col: 'timestamp'|'area'|'username'|'severity') => {
    if (sort === col) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(col); setOrder('desc');
    }
    setTimeout(load, 0);
  };

  const rows = useMemo(() => items, [items]);

  return (
    <div className="p-8 text-white bg-gray-900 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-4">Feedback</h1>
        <p className="text-gray-400 mb-6">View and export user feedback. Filter by date, area, severity, and user. Sort columns by clicking headers.</p>

        {error && (
          <Alert variant="error" className="mb-4">{error}</Alert>
        )}

        <Card className="bg-gray-800 border border-gray-700 mb-6">
          <div className="p-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
            <div>
              <label className="block text-sm text-gray-300 mb-1">Start (UTC)</label>
              <input type="date" className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2" value={filters.start} onChange={(e)=>setFilters({...filters, start: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">End (UTC)</label>
              <input type="date" className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2" value={filters.end} onChange={(e)=>setFilters({...filters, end: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Area</label>
              <select className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2" value={filters.area} onChange={(e)=>setFilters({...filters, area: e.target.value})}>
                <option value="">Any</option>
                {areas.map(a => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">User</label>
              <select className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2" value={filters.username} onChange={(e)=>setFilters({...filters, username: e.target.value})}>
                <option value="">Any</option>
                {users.map(u => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Severity</label>
              <select className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2" value={filters.severity} onChange={(e)=>setFilters({...filters, severity: e.target.value})}>
                <option value="">Any</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div className="flex gap-2">
              <Button onClick={load} disabled={loading}>Apply</Button>
              <Button onClick={()=>{setFilters({start:'',end:'',area:'',username:'',severity:''}); setTimeout(load,0);}} className="bg-gray-700 hover:bg-gray-600">Reset</Button>
            </div>
          </div>
        </Card>

        <div className="flex items-center justify-between mb-3">
          <div className="text-gray-400 text-sm">{rows.length} records</div>
          <div className="flex gap-2">
            <Button onClick={()=>onExport('json')} className="bg-gray-700 hover:bg-gray-600">Download JSON (NDJSON)</Button>
            <Button onClick={()=>onExport('csv')} className="bg-blue-600 hover:bg-blue-700">Download CSV</Button>
          </div>
        </div>

        <div className="overflow-auto border border-gray-700 rounded">
          <table className="min-w-full">
            <thead className="bg-gray-800">
              <tr>
                <th className="p-3 text-left cursor-pointer" onClick={()=>onSort('timestamp')}>Time {sort==='timestamp' ? (order==='asc'?'↑':'↓') : ''}</th>
                <th className="p-3 text-left cursor-pointer" onClick={()=>onSort('username')}>User {sort==='username' ? (order==='asc'?'↑':'↓') : ''}</th>
                <th className="p-3 text-left cursor-pointer" onClick={()=>onSort('area')}>Area {sort==='area' ? (order==='asc'?'↑':'↓') : ''}</th>
                <th className="p-3 text-left cursor-pointer" onClick={()=>onSort('severity')}>Severity {sort==='severity' ? (order==='asc'?'↑':'↓') : ''}</th>
                <th className="p-3 text-left">Blocks</th>
                <th className="p-3 text-left">Route</th>
                <th className="p-3 text-left">Notes</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r)=> (
                <tr key={r.id} className="border-t border-gray-800 hover:bg-gray-800/50">
                  <td className="p-3 text-gray-300 whitespace-nowrap">{r.timestamp?.replace('T',' ').replace('Z','')}</td>
                  <td className="p-3 text-gray-300">{r.username || '-'}</td>
                  <td className="p-3 text-gray-300">{r.area || '-'}</td>
                  <td className="p-3 text-gray-300 capitalize">{r.severity || '-'}</td>
                  <td className="p-3 text-gray-300">{r.blocks_progress ? 'Yes' : 'No'}</td>
                  <td className="p-3 text-gray-300">{r.route || '-'}</td>
                  <td className="p-3 text-gray-400 max-w-[480px] truncate" title={r.notes}>{r.notes}</td>
                </tr>
              ))}
              {rows.length === 0 && !loading && (
                <tr>
                  <td className="p-6 text-center text-gray-500" colSpan={7}>No feedback records</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
